import { createApi } from '@reduxjs/toolkit/query/react';
import { PaginatedApiResponse } from 'domain/Common';
import { axiosBaseQuery } from 'modules/api/utils';
import { UrlNames } from 'modules/app/constants';
import { configService } from 'modules/app/services';

import { mapNatterDetailFromApi } from './mappers';
import {
  CreateNatterPayload,
  GetNatterListPayload,
  Natter,
  NatterListItem,
  NatterPublic,
  PostAvailabilityPayload,
  PostAvailabilityResponse,
} from './types';

const getBaseURL = () => configService.getUrl(UrlNames.OpenNatterApi);

export const nattersApi = createApi({
  reducerPath: 'nattersApi',
  tagTypes: ['NATTERS_LIST', 'NATTER_DETAILS'],
  baseQuery: axiosBaseQuery({ baseURL: getBaseURL }),
  endpoints: (builder) => ({
    getNattersList: builder.query<PaginatedApiResponse<NatterListItem>, GetNatterListPayload>({
      providesTags: ['NATTERS_LIST'],
      query: ({ communityId, page, size, searchText, statuses }) => ({
        url: '/open-natters',
        params: { page, size, communityId, searchText, statuses },
      }),
    }),
    createNatter: builder.mutation<Natter, CreateNatterPayload>({
      query: (data) => ({
        url: '/open-natters',
        method: 'POST',
        data,
      }),
      transformResponse: mapNatterDetailFromApi,
      invalidatesTags: ['NATTERS_LIST'],
    }),
    getNatter: builder.query<Natter, string>({
      providesTags: (_result, _error, arg) => [{ type: 'NATTER_DETAILS', id: arg }],
      query: (id) => ({
        url: `/open-natters/${id}`,
      }),
      transformResponse: mapNatterDetailFromApi,
    }),
    getPublicNatter: builder.query<NatterPublic, string>({
      providesTags: (_result, _error, arg) => [{ type: 'NATTER_DETAILS', id: arg }],
      query: (id) => ({
        url: `/public/open-natters/${id}`,
      }),
      transformResponse: (response: NatterPublic, _meta, id) => ({ ...response, id }),
    }),
    postAvailability: builder.mutation<PostAvailabilityResponse, PostAvailabilityPayload>({
      query: ({ id, ...data }) => ({
        url: `/open-natters/${id}/availability`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'NATTER_DETAILS', id }],
    }),
  }),
});

export const {
  useGetNattersListQuery,
  useCreateNatterMutation,
  useGetNatterQuery,
  useGetPublicNatterQuery,
  usePostAvailabilityMutation,
} = nattersApi;
